import { useMemo, useState } from 'react'
import { Alert, Button, Input, Modal, Select, Space, Table, Tag, message } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { engineService, type EngineTagRow, type EngineTagStatus } from '@/services/engine.service'
import { materialMasterService } from '@/services/materialMaster.service'
import { MaterialSkuSelect } from '@/components/rfid/MaterialSkuSelect'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { formatDateTime } from '@/utils/cn'
import { materialMasterDropdownOptions } from '@/utils/materialMaster'
import { useRegionStore } from '@/stores/regionStore'

const COUNTRY_LABEL: Record<string, string> = {
  '01': 'India',
  '02': 'UAE',
  '03': 'KSA',
}

export default function RfidTagsPage() {
  const queryClient = useQueryClient()
  const regionView = useRegionStore((s) => s.view)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [status, setStatus] = useState<EngineTagStatus | ''>('')
  const [country, setCountry] = useState('')
  const [qInput, setQInput] = useState('')
  const [q, setQ] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [lastFile, setLastFile] = useState<string | null>(null)

  const [remapTarget, setRemapTarget] = useState<EngineTagRow | null>(null)
  const [remapSku, setRemapSku] = useState('')

  const listFilters = useMemo(
    () => ({
      status,
      country_code: country,
      q,
    }),
    [status, country, q]
  )

  const listQ = useQuery({
    queryKey: ['engine-rfid-tags', page, pageSize, status, country, q],
    queryFn: () =>
      engineService.listTags({
        page,
        limit: pageSize,
        ...listFilters,
      }),
  })

  const materialsQ = useQuery({
    queryKey: ['engine-material-master', regionView],
    queryFn: () => materialMasterService.fetchMaterialDetails(''),
    staleTime: 5 * 60_000,
    enabled: !!remapTarget,
  })

  const materialOptions = useMemo(
    () => materialMasterDropdownOptions(materialsQ.data ?? []),
    [materialsQ.data]
  )

  const onDownloadReport = async () => {
    setDownloading(true)
    try {
      const { filename, count } = await engineService.downloadTagMappingReport(listFilters)
      setLastFile(filename)
      message.success(`Downloaded ${count} tag row${count === 1 ? '' : 's'} → ${filename}`)
    } catch (err) {
      message.error((err as { message?: string })?.message || 'Tag mapping report download failed')
    } finally {
      setDownloading(false)
    }
  }
  const remapMu = useMutation({
    mutationFn: ({ rfid, sku }: { rfid: string; sku: string }) =>
      engineService.tagOne(rfid, sku),
    onSuccess: (row) => {
      if (row.remapped) {
        message.success(
          `Remapped ${row.rfid_code}: ${row.previous_sku_code || '—'} → ${row.sku_code}`
        )
      } else if (row.created === false) {
        message.success(`Already mapped to ${row.sku_code}`)
      } else {
        message.success(`Mapped ${row.rfid_code} → ${row.sku_code}`)
      }
      setRemapTarget(null)
      setRemapSku('')
      void queryClient.invalidateQueries({ queryKey: ['engine-rfid-tags'] })
    },
    onError: (err: { message?: string }) => {
      message.error(err?.message || 'Remap failed')
    },
  })

  const columns: ColumnsType<EngineTagRow> = useMemo(
    () => [
      {
        title: 'RFID code',
        dataIndex: 'rfid_code',
        key: 'rfid_code',
        fixed: 'left',
        width: 220,
        render: (v: string) => <span className="font-mono text-xs">{v}</span>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (v: EngineTagStatus) => (
          <Tag color={v === 'tagged' ? 'green' : 'blue'}>{v}</Tag>
        ),
      },
      {
        title: 'SKU',
        dataIndex: 'sku_code',
        key: 'sku_code',
        width: 140,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Country',
        dataIndex: 'country_code',
        key: 'country_code',
        width: 100,
        render: (v: string) => COUNTRY_LABEL[v] ?? v,
      },
      {
        title: 'MMYY',
        dataIndex: 'month_year',
        key: 'month_year',
        width: 80,
      },
      {
        title: 'Serial',
        dataIndex: 'serial',
        key: 'serial',
        width: 120,
        render: (v: string) => <span className="font-mono text-xs">{v}</span>,
      },
      {
        title: 'Birthed',
        dataIndex: 'birthed_at',
        key: 'birthed_at',
        width: 170,
        render: (v: string | null) => (v ? formatDateTime(v) : '—'),
      },
      {
        title: 'Tagged',
        dataIndex: 'tagged_at',
        key: 'tagged_at',
        width: 170,
        render: (v: string | null) => (v ? formatDateTime(v) : '—'),
      },
      {
        title: 'Birthed by',
        dataIndex: 'birthed_by',
        key: 'birthed_by',
        width: 120,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Tagged by',
        dataIndex: 'tagged_by',
        key: 'tagged_by',
        width: 120,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 120,
        render: (_: unknown, row: EngineTagRow) => (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setRemapTarget(row)
              setRemapSku(row.sku_code ?? '')
            }}
          >
            {row.status === 'tagged' ? 'Remap SKU' : 'Map SKU'}
          </Button>
        ),
      },
    ],
    []
  )

  const onTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1)
    setPageSize(pagination.pageSize ?? 25)
  }

  const onConfirmRemap = () => {
    if (!remapTarget) return
    const sku = remapSku.trim()
    if (!sku) {
      message.error('Select a SKU from material master')
      return
    }
    remapMu.mutate({ rfid: remapTarget.rfid_code, sku })
  }

  return (
    <PageContainer fluid>
      <PageHeader
        title="RFID tags master"
        subtitle="Engine registry: RFID ↔ SKU status, mapping, and remap. Download a full filtered report as CSV (opens in Excel)."
      />

      {listQ.isError ? (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          message="Could not load RFID tags"
          description={(listQ.error as { message?: string })?.message}
        />
      ) : null}

      {lastFile ? (
        <Alert
          className="mb-4"
          type="success"
          showIcon
          closable
          onClose={() => setLastFile(null)}
          message={`Last download: ${lastFile}`}
        />
      ) : null}

      <SectionCard title="Filters & report" className="mb-5">
        <Space wrap size="middle" className="w-full items-center">
          <Input.Search
            allowClear
            placeholder="Search RFID, SKU, or serial"
            className="min-w-[260px] w-[320px] max-w-full"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onSearch={(value) => {
              setPage(1)
              setQ(value.trim())
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            className="w-[140px]"
            value={status || undefined}
            onChange={(v) => {
              setPage(1)
              setStatus((v as EngineTagStatus | undefined) ?? '')
            }}
            options={[
              { value: 'birthed', label: 'Birthed' },
              { value: 'tagged', label: 'Tagged' },
            ]}
          />
          <Select
            allowClear
            placeholder="Country"
            className="w-[140px]"
            value={country || undefined}
            onChange={(v) => {
              setPage(1)
              setCountry(v ?? '')
            }}
            options={[
              { value: '01', label: 'India' },
              { value: '02', label: 'UAE' },
              { value: '03', label: 'KSA' },
            ]}
          />
          <Button
            icon={<Download className="h-4 w-4" />}
            loading={downloading}
            onClick={() => void onDownloadReport()}
          >
            Download filtered report
          </Button>
        </Space>
        <p className="mb-0 mt-3 text-xs text-muted-foreground">
          Report columns: RFID code, status (birthed/tagged), whether a SKU is mapped, SKU code,
          country, serial, birth/tag timestamps and actors. Uses the filters above (empty filters =
          all tags).
        </p>
      </SectionCard>

      <SectionCard title="Tags" variant="table">
        <Table<EngineTagRow>
          rowKey="rfid_code"
          size="small"
          scroll={{ x: 1520 }}
          loading={listQ.isLoading || listQ.isFetching}
          columns={columns}
          dataSource={listQ.data?.data ?? []}
          onChange={onTableChange}
          pagination={{
            current: page,
            pageSize,
            total: listQ.data?.pagination.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            showTotal: (total) => `${total} tag${total === 1 ? '' : 's'}`,
          }}
        />
      </SectionCard>

      <Modal
        title={remapTarget?.status === 'tagged' ? 'Remap SKU' : 'Map SKU'}
        open={!!remapTarget}
        onCancel={() => {
          if (remapMu.isPending) return
          setRemapTarget(null)
          setRemapSku('')
        }}
        onOk={onConfirmRemap}
        okText={remapTarget?.status === 'tagged' ? 'Remap' : 'Map'}
        confirmLoading={remapMu.isPending}
        okButtonProps={{ disabled: !remapSku.trim() || materialsQ.isLoading }}
        destroyOnClose
      >
        {remapTarget ? (
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-muted-foreground">RFID</div>
              <div className="font-mono text-sm">{remapTarget.rfid_code}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground">Current SKU</div>
              <div className="text-sm">{remapTarget.sku_code || '— (birthed only)'}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold text-muted-foreground">New SKU</div>
              <MaterialSkuSelect
                autoFocus
                value={remapSku}
                options={materialOptions}
                loading={materialsQ.isLoading || materialsQ.isFetching}
                onChange={setRemapSku}
                placeholder="Enter SKU / material code"
                disabled={remapMu.isPending}
              />
              {materialsQ.isError ? (
                <p className="mb-0 mt-2 text-xs font-semibold text-[var(--leap-primary)]">
                  {(materialsQ.error as { message?: string })?.message ||
                    'Failed to load material master'}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>
    </PageContainer>
  )
}
